import { useGetSessionsAnalyticsActivityQuery } from '@carboncopyco/data';
import type { ResourceType } from '@carboncopyco/types';
import { useParams } from 'react-router-dom';
import { calculatePercentageDifference, extractDataForChart } from '../Charts';
import { useConsoleState } from './ConsoleContext';

type UseGetAnalyticsCardCommonDataProps = {
  resourceTypes: ResourceType[];
};

export const useGetAnalyticsCardCommonData = ({
  resourceTypes,
}: UseGetAnalyticsCardCommonDataProps) => {
  const { organizationId } = useParams();
  const { startTime, prevEndTime, prevStartTime, endTime, timezone } =
    useConsoleState();

  const { data: prevData, isLoading: isLoadingPrev } =
    useGetSessionsAnalyticsActivityQuery({
      organizationId,
      resourceTypes,
      start: prevStartTime,
      end: prevEndTime,
      timezone,
      resolution: 'summary',
      shortSessions: true,
      internalSessions: true,
    });
  const prevOccurrences = prevData?.results?.[0]?.values?.occurrences;

  const { data: currentData, isLoading: isLoadingCurrent } =
    useGetSessionsAnalyticsActivityQuery({
      organizationId,
      resourceTypes,
      start: startTime,
      end: endTime,
      timezone,
      resolution: 'summary',
      shortSessions: true,
      internalSessions: true,
    });
  const currentOccurrences = currentData?.results?.[0]?.values?.occurrences;

  const { data: chartData, isLoading: isLoadingChartData } =
    useGetSessionsAnalyticsActivityQuery({
      organizationId,
      resourceTypes,
      start: startTime,
      end: endTime,
      timezone,
      resolution: 'day',
      shortSessions: true,
      internalSessions: true,
    });

  const chart = extractDataForChart(chartData?.results);

  const trend =
    typeof currentOccurrences === 'number' &&
    typeof prevOccurrences === 'number'
      ? calculatePercentageDifference(currentOccurrences, prevOccurrences)
      : null;

  const isLoading = isLoadingChartData || isLoadingCurrent || isLoadingPrev;

  return { chart, trend, isLoading };
};
